/**
 * exp-adhoc-form (US-AIF.P2-0252-S02 · @UC9430 · @gate money,ai) — FORM CHI NỀN TẢNG + seam split.
 *
 * Đây là task NỀN Phase 2 CHI: mở đường AI form tạo khoản chi (expense) NHẬN khối split có cấu trúc,
 * TÁI DÙNG engine split-compute đã có ở ExpenseService (KHÔNG viết lại phép tiền). Task này KHÔNG định
 * nghĩa field-seq đầy đủ của từng usecase EXP (0253/0254 lo — peer/mixed/exempt/…). Ở đây chỉ:
 *  - EXP-ADHOC: usecase CHI ĐƠN mặc định (fund_flow — quỹ trả 100%, 0 participant). Chuỗi field tất-định
 *    [fund_picker(fundId) → text(name) → money(amount)]. Đây là "vertical slice" tối thiểu để chạy được
 *    bridge confirmExpenseForm end-to-end (mirror COL-EQUAL cho đường thu).
 *  - `gatherExpenseSplitFromForm`: SEAM gom khối split từ formState. EXP-ADHOC ⇒ fund_flow rỗng. Usecase
 *    EXP có split (0253/0254) cung cấp field-seq + đọc participants qua seam này KHÔNG đổi bridge.
 *
 * HÀM THUẦN (DP-3): KHÔNG DB, KHÔNG LLM, KHÔNG Date.now. Money = string minor VND (B1 — no-float, >0).
 * fund_picker options do SERVICE nạp (quỹ THẬT của user) — domain chỉ validate value ∈ options.
 */
import { DomainError } from "../errors.js";
import { validateFieldValue, type SlotFieldSpec, type SlotOption, type FormState } from "./form-slot-filling.js";

/** intentCode EXP-ADHOC (chi đơn — quỹ trả 100%). Foundation Phase 2 CHI. */
export const EXP_ADHOC_INTENT_CODE = "EXP-ADHOC";

/** specVersion form EXP-ADHOC (tăng khi đổi chuỗi field — session lưu để phát hiện drift). */
export const EXP_ADHOC_SPEC_VERSION = 1;

/**
 * 🔴 US-AIF.P2-0253-S01 (@UC9432) — intentCode EXP-F-NOALLOC ("chi chung KHÔNG phân bổ"): quỹ trả 100%,
 * KHÔNG chia cho ai (memberId=null). Đây là usecase CHI mặc định user-facing (real_scenarios.md ✅
 * EXP-F-NOALLOC = "hành vi mặc định hiện tại"). Split GIỐNG HỆT EXP-ADHOC (fund_flow rỗng); khác chỉ ở
 * NHÃN + là entry catalog admin bật/tắt được RIÊNG. EXP-ADHOC là foundation ẨN (không vào catalog).
 */
export const EXP_F_NOALLOC_INTENT_CODE = "EXP-F-NOALLOC";

/** specVersion form EXP-F-NOALLOC (độc lập EXP-ADHOC — session lưu để phát hiện drift chuỗi field). */
export const EXP_F_NOALLOC_SPEC_VERSION = 1;

/**
 * 🔴 Trần PG signed BIGINT (mirror COL_EQUAL_PG_BIGINT_MAX / contracts). amount > trần ⇒ ghi DB P2020
 * → 500. Domain chốt TRƯỚC ⇒ 400. NHÂN BẢN literal y hệt (domain không dep contracts) — đổi 1 bên đổi cả.
 */
export const EXP_ADHOC_PG_BIGINT_MAX = 9223372036854775807n;

/** Trần độ dài tên khoản chi — mirror HTTP contract `.max(200)` (createExpenseRequestSchema.name). */
export const EXP_ADHOC_NAME_MAX_LENGTH = 200;

/** Thứ tự field EXP-ADHOC (tất-định — thứ tự hỏi). Test khoá thứ tự này (contract nghiệp vụ). */
export const EXP_ADHOC_FIELD_ORDER = ["fundId", "name", "amount"] as const;

/**
 * Nguồn gánh khoản chi (mirror contracts EXPENSE_SOURCE — domain không dep contracts).
 *
 * 🔴 US-AIF.P2-0253c (@UC9433): FUND_FLOW_ALLOCATED = quỹ trả 100% CASH NHƯNG chia GÁNH NẶNG minh bạch
 * cho từng thành viên (Σshare = amount), **KHÔNG sinh nợ** (khác peer/mixed). Nền cho EXP-F-EQUAL.
 * Thêm mã ⇒ PHẢI thêm cả ở contracts `EXPENSE_SOURCE` (nhân bản literal — đổi 1 bên đổi cả hai).
 */
export const EXP_SOURCE = {
  FUND_FLOW: "fund_flow",
  PEER: "peer",
  MIXED: "mixed",
  FUND_FLOW_ALLOCATED: "fund_flow_allocated",
} as const;
export type ExpSource = (typeof EXP_SOURCE)[keyof typeof EXP_SOURCE];

/** Alias tiện dụng cho source allocated (dùng ở exp-f-equal-form — tránh lặp chuỗi literal). */
export const EXP_SOURCE_ALLOCATED = EXP_SOURCE.FUND_FLOW_ALLOCATED;

/** 1 participant đã gom từ form (subject + weight/fixed/exempt/share). Đồng shape service input. */
export interface ExpFormParticipant {
  readonly groupMemberId?: string;
  readonly dependentId?: string;
  readonly weight?: number;
  readonly fixedMinor?: string;
  readonly shareMinor?: string;
  readonly isExempt?: boolean;
}

/**
 * Khối split đã gom từ form — nối THẲNG vào ExpenseService.create (source/splitRule/participants/…).
 * EXP-ADHOC ⇒ source=fund_flow, participants=[] (quỹ trả 100%). Usecase EXP split (0253/0254) trả khối
 * đầy đủ (source=peer/mixed, participants[], payerMemberId, fundPortionMinor).
 */
export interface ExpFormSplit {
  readonly source: ExpSource;
  readonly splitRule: string;
  readonly participants: readonly ExpFormParticipant[];
  readonly payerMemberId?: string;
  readonly fundPortionMinor?: string;
}

/** Payload EXP-ADHOC đã validate — nối vào CreateExpenseInput (fundId/name/amountMinor + split). */
export interface ExpAdhocPayload {
  readonly fundId: string;
  readonly name: string;
  /** STRING số nguyên dương minor (B1 — service parse → BigInt). */
  readonly amountMinor: string;
  /** fund_flow rỗng (EXP-ADHOC quỹ trả 100%). */
  readonly split: ExpFormSplit;
}

/**
 * Dựng chuỗi field EXP-ADHOC với options quỹ THẬT nạp vào fund_picker. Immutable (copy options).
 * MỌI field required. Chuỗi tất-định [fund_picker(fundId) → text(name) → money(amount)].
 */
export function buildExpAdhocFields(fundOptions: readonly SlotOption[]): SlotFieldSpec[] {
  return [
    { key: "fundId", type: "fund_picker", required: true, options: [...fundOptions] },
    { key: "name", type: "text", required: true },
    { key: "amount", type: "money", required: true },
  ];
}

/** Ném DomainError REQUIRED_FIELD_MISSING cho field bắt buộc còn trống. */
function assertPresent(value: string | null | undefined, field: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new DomainError("REQUIRED_FIELD_MISSING", `Thiếu thông tin bắt buộc: ${field}.`);
  }
  return value.trim();
}

/**
 * SEAM gom khối split từ formState theo intentCode. EXP-ADHOC ⇒ fund_flow rỗng (quỹ trả 100%). Usecase
 * EXP có split (0253/0254) OVERRIDE hàm này / thêm nhánh intentCode ĐỌC participants từ formState. Bridge
 * `confirmExpenseForm` gọi seam này ⇒ KHÔNG đổi khi thêm usecase split.
 */
export function gatherExpenseSplitFromForm(intentCode: string, _formState: FormState): ExpFormSplit {
  // Foundation: EXP-ADHOC + EXP-F-NOALLOC ⇒ fund_flow rỗng (quỹ trả 100%, KHÔNG phân bổ). Cùng shape
  // split — EXP-F-NOALLOC là bản user-facing của EXP-ADHOC (0253). Nhánh peer/mixed do 0254+ thêm.
  if (intentCode === EXP_ADHOC_INTENT_CODE || intentCode === EXP_F_NOALLOC_INTENT_CODE) {
    return { source: EXP_SOURCE.FUND_FLOW, splitRule: "equal", participants: [] };
  }
  // intentCode expense lạ (chưa nối) ⇒ fail-loud: KHÔNG đoán split (tránh tạo chi sai nguồn).
  throw new DomainError(
    "REQUIRED_FIELD_MISSING",
    `Chưa hỗ trợ gom khối chia cho form "${intentCode}".`,
  );
}

/**
 * Validate CẢ BỘ formState EXP-ADHOC → ExpAdhocPayload chuẩn hoá (fail-fast, KHÔNG partial).
 *
 * Thứ tự chốt (tất-định):
 *  1. fundId có + ∈ options (chống IDOR quỹ lạ ở LỚP domain — service còn re-verify RBAC thật).
 *  2. name non-empty (trim) + ≤ trần độ dài.
 *  3. amount money minor no-float (validateFieldValue) + >0 + ≤ trần BIGINT.
 * Ném DomainError (map 400) cho MỌI vi phạm. split = fund_flow rỗng (quỹ trả 100%).
 */
export function validateExpAdhocForm(
  formState: FormState,
  fundOptions: readonly SlotOption[],
): ExpAdhocPayload {
  // 1) fundId ∈ options (domain-level IDOR guard — service re-verify RBAC thật ở confirm).
  const fundId = assertPresent(formState.fundId, "fundId");
  const allowedFundIds = new Set(fundOptions.map((o) => o.value));
  if (!allowedFundIds.has(fundId)) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      "Quỹ được chọn không nằm trong danh sách quỹ của bạn.",
    );
  }

  // 2) name non-empty + ≤ trần độ dài (mirror HTTP .max(200)).
  const name = assertPresent(formState.name, "name");
  if (name.length > EXP_ADHOC_NAME_MAX_LENGTH) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      `Tên khoản chi quá dài (tối đa ${EXP_ADHOC_NAME_MAX_LENGTH} ký tự).`,
    );
  }

  // 3) amount money minor no-float + >0 + ≤ trần BIGINT.
  const amountRaw = assertPresent(formState.amount, "amount");
  const amountValidation = validateFieldValue(
    { key: "amount", type: "money", required: true },
    amountRaw,
  );
  if (!amountValidation.ok) {
    throw new DomainError("LEDGER_AMOUNT_NOT_POSITIVE", amountValidation.message);
  }
  if (BigInt(amountValidation.value) <= 0n) {
    throw new DomainError("LEDGER_AMOUNT_NOT_POSITIVE", "Số tiền phải lớn hơn 0.");
  }
  if (BigInt(amountValidation.value) > EXP_ADHOC_PG_BIGINT_MAX) {
    throw new DomainError("LEDGER_AMOUNT_NOT_POSITIVE", "Số tiền vượt quá giới hạn cho phép.");
  }

  return {
    fundId,
    name,
    amountMinor: amountValidation.value,
    split: gatherExpenseSplitFromForm(EXP_ADHOC_INTENT_CODE, formState),
  };
}

/**
 * 🔴 US-AIF.P2-0253-S01 (@UC9432) — chuỗi field EXP-F-NOALLOC. GIỐNG HỆT EXP-ADHOC
 * [fund_picker(fundId) → text(name) → money(amount)] — reuse builder (KHÔNG nhân bản). Có wrapper riêng
 * để catalog/service tham chiếu qua intentCode EXP-F-NOALLOC (nhãn user-facing "chi chung không phân bổ").
 */
export function buildExpFNoallocFields(fundOptions: readonly SlotOption[]): SlotFieldSpec[] {
  return buildExpAdhocFields(fundOptions);
}

/**
 * 🔴 US-AIF.P2-0253-S01 (@UC9432) — validate formState EXP-F-NOALLOC → ExpAdhocPayload. Reuse toàn bộ luật
 * EXP-ADHOC (fundId ∈ options IDOR, name ≤ trần, amount no-float/>0/≤trần BIGINT — B1). split = fund_flow
 * rỗng gom qua intentCode EXP-F-NOALLOC (quỹ trả 100%, participants=[]). KHÔNG money-math mới.
 */
export function validateExpFNoallocForm(
  formState: FormState,
  fundOptions: readonly SlotOption[],
): ExpAdhocPayload {
  const base = validateExpAdhocForm(formState, fundOptions);
  return { ...base, split: gatherExpenseSplitFromForm(EXP_F_NOALLOC_INTENT_CODE, formState) };
}
